import { useState } from 'react';
import { Download } from 'lucide-react';
import { toast } from 'sonner';
import { Button, ScrollArea, Separator } from 'shared-ui';

import { useClient } from '@/clients/useClient';
import { fetchHighRiskAirValves } from '@/api/airValvesApi';
import { exportHighRiskAirValves } from '@/utils/airValveExport';
import { type ExportFormat } from '@/utils/exportWorkbook';
import { useDownloadFileName } from '@/hooks/useDownloadFileName';
import { DownloadFileNameField } from '@/components/shared/DownloadFileNameField';
import { DownloadFormatField } from '@/components/shared/DownloadFormatField';
import { PanelCloseButton, SlidingPanel } from '@/components/shared/SlidingPanel';

export interface DownloadHighRiskPanelProps {
    isOpen: boolean;
    onClose: () => void;
}

function DownloadHighRiskPanelContent({ onClose }: Omit<DownloadHighRiskPanelProps, 'isOpen'>) {
    const { selectedClient } = useClient();
    const {
        fileName,
        setFileName,
        error: fileNameError,
        isValid: fileNameValid,
    } = useDownloadFileName('High Risk Air Valves', selectedClient?.name, { prefill: false });
    const [format, setFormat] = useState<ExportFormat>('xlsx');
    const [isExporting, setIsExporting] = useState(false);

    const canDownload = selectedClient != null && !isExporting && fileNameValid;

    async function handleDownload() {
        if (!canDownload) return;

        setIsExporting(true);
        const toastId = toast.loading('Preparing download', {
            description: 'Fetching high-risk air valves. This may take a moment for large datasets.',
        });

        try {
            const data = await fetchHighRiskAirValves(selectedClient.id);
            const downloaded = await exportHighRiskAirValves(data, fileName.trim(), format);

            if (downloaded) {
                toast.success('Download ready', {
                    id: toastId,
                    description: 'Your high-risk air valves file has been downloaded.',
                });
            } else {
                toast.info('No data to export', {
                    id: toastId,
                    description: 'There are no high-risk air valves for this client.',
                });
            }
        } catch {
            toast.error('Download failed', {
                id: toastId,
                description: 'Something went wrong preparing the download. Please try again.',
            });
        } finally {
            setIsExporting(false);
        }
    }

    return (
        <>
            {/* Header — fixed 80px height (h-20), does not scroll */}
            <div className="flex h-20 shrink-0 items-center justify-between px-6">
                <h2 className="text-lg leading-7 font-black tracking-normal uppercase md:text-2xl md:leading-8">
                    Download High Risk
                </h2>
                <PanelCloseButton onClick={onClose} aria-label="Close download high risk panel" />
            </div>

            <Separator />

            <ScrollArea className="min-h-0 flex-1">
                <div className="space-y-6 px-6 py-4">
                    <DownloadFormatField id="download-hr-format" value={format} onChange={setFormat} />

                    <DownloadFileNameField
                        id="download-hr-filename"
                        value={fileName}
                        error={fileNameError}
                        placeholder={`E.g ${selectedClient?.name ?? 'Client Name'} High Risk Air Valves`}
                        onChange={setFileName}
                    />
                </div>
            </ScrollArea>

            {/* Sticky footer — Download (primary) + Cancel (secondary), always visible */}
            <div className="shrink-0 space-y-2 border-t px-6 py-4">
                <Button
                    className="h-10 w-full cursor-pointer"
                    type="button"
                    onClick={() => void handleDownload()}
                    disabled={!canDownload}
                >
                    <Download className="size-4" strokeWidth={2} />
                    Download
                </Button>
                <Button
                    variant="ghost"
                    type="button"
                    className="panel-close-btn text-primary h-10 w-full cursor-pointer"
                    onClick={onClose}
                >
                    Cancel
                </Button>
            </div>
        </>
    );
}

export function DownloadHighRiskPanel(props: DownloadHighRiskPanelProps) {
    return (
        <SlidingPanel
            isOpen={props.isOpen}
            onClose={props.onClose}
            ariaLabel="Download high-risk air valves"
            title="Download High Risk"
            widthClassName="w-[377px]"
        >
            <DownloadHighRiskPanelContent {...props} />
        </SlidingPanel>
    );
}
